/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class CheckoutService {
    /**
     * Section is valid set next button as active.
     */
    public isStepValidEmitter: Subject<boolean> = new Subject<boolean>();

    /**
     * Emits the parent step section name and index.
     */
    public activeStepEmitter: Subject<any> = new Subject<any>();

    /**
     * If triggered the child component should submit its internal form.
     */
    public childSubmitEmitter: Subject<number> = new Subject<number>();

    /**
     * Emit the number of items in the cart.
     */
    public cartItemsEmitter: Subject<number> = new Subject<number>();

    /**
     * Active step index.
     * @private
     */
    private _activeStep: number = 0;

    /**
     * Cart stored products
     * @private
     */
    private _cartItems: any[] = [];

    /**
     * Cart items count.
     * @private
     */
    private _cartItemsCount: number = 0;

    /**
     * Store selected card id.
     * @private
     */
    private _selectedCardId: string;

    constructor() {
        try {
            this.getLocalStorageCartItems();
        } catch (error) {
            this._cartItems = [];
        }
    }

    get activeStep(): number {
        return this._activeStep;
    }

    set cartItems(cartItem: any) {
        const findIndex: any = this.cartItems.findIndex((item: any): boolean => item.price.id === cartItem.price.id);
        if (findIndex !== -1) {
            this._cartItems[findIndex].quantity = this._cartItems[findIndex].quantity + 1;
        } else {
            this._cartItems.push(cartItem);
        }

        this.isStepValidEmitter.next(!!this.cartItems?.length);
        localStorage.setItem('cart', JSON.stringify(this.cartItems));
        this.emitCartItemsTotal(this.cartItems);
    }

    get cartItems(): any {
        return this._cartItems;
    }

    set selectedCardId(selectedCardId: string) {
        this._selectedCardId = selectedCardId;
        this.isStepValidEmitter.next(!!this.selectedCardId?.length);
    }

    get selectedCardId(): string {
        return this._selectedCardId;
    }

    set cartItemsCount(total: number) {
        this._cartItemsCount = total;
    }

    get cartItemsCount(): number {
        return this._cartItemsCount;
    }

    /**
     * Get local storage cart items.
     */
    public getLocalStorageCartItems(): void {
        const cartItems: any[] = JSON.parse(localStorage.getItem('cart'));
        if (cartItems?.length) {
            this._cartItems = cartItems;
            this.emitCartItemsTotal(this._cartItems);
        }
    }

    /**
     * Decrement the active step index
     */
    public backStep(): void {
        this._activeStep--;
        this.activeStepEmitter.next(this._activeStep);
    }

    /**
     * Increment the active step index
     */
    public nextStep(): void {
        this._activeStep++;
        this.activeStepEmitter.next(this._activeStep);
    }

    /**
     * Increment the active step index
     * @param step
     */
    public setStep(step: number): void {
        this._activeStep = step;
        this.activeStepEmitter.next(this._activeStep);
    }

    /**
     * Delete item from cart.
     * @param selectedProduct
     */
    public deleteCartItem(selectedProduct: any): void {
        const deleteIndex: any = this.cartItems.findIndex((item: any): boolean => item.price.id === selectedProduct.price.id);
        if (deleteIndex !== -1) {
            this.cartItems.splice(deleteIndex, 1);
            this.isStepValidEmitter.next(!!this.cartItems?.length);
            localStorage.setItem('cart', JSON.stringify(this.cartItems));
            this.emitCartItemsTotal(this.cartItems);
        }
    }

    /**
     * Empty cart items.
     */
    public emptyCartItems(): void {
        this._cartItems = [];
        localStorage.setItem('cart', JSON.stringify([]));
        this.emitCartItemsTotal([]);
    }

    /**
     * Emit card items total.
     * @param cartItems
     * @private
     */
    private emitCartItemsTotal(cartItems: any[]): void {
        const total: number = cartItems?.length
            ? cartItems.reduce((a, b) => a + Number(b['quantity']) || 0, 0)
            : 0;
        this.cartItemsCount = total;
        this.cartItemsEmitter.next(total);
    }
}
